import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, BookOpen, Award, GraduationCap, PlayCircle, Leaf, CalendarClock } from "lucide-react";
import { Progress } from "../components/ui/progress";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DisclaimerBanner } from "../components/Layout";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const enrolled = user?.access_active || user?.role === "admin";
  const expired = user?.access_expired;

  const load = useCallback(async () => {
    const [ov, pr] = await Promise.all([
      api.get("/course/overview"),
      enrolled ? api.get("/course/progress") : Promise.resolve({ data: null }),
    ]);
    setOverview(ov.data);
    setProgress(pr.data);
  }, [enrolled]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const enroll = async () => {
    setEnrolling(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setEnrolling(false);
    }
  };

  if (!overview) return <div className="max-w-6xl mx-auto px-4 py-16 text-[#5A6B61]">Loading…</div>;

  // Enrollment gate
  if (!enrolled) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <span className="w-14 h-14 mx-auto rounded-full bg-[#2C4A3E] flex items-center justify-center">
          <Lock className="w-7 h-7 text-[#E8ECE8]" />
        </span>
        <h1 className="mt-6 font-serif text-3xl sm:text-4xl text-[#1A2621]">
          {expired ? "Your access has expired" : "Unlock the full course"}
        </h1>
        <p className="mt-3 text-[#5A6B61]">
          {expired ? (
            <>Your one-year access to the course has ended. Re-enroll for ${overview.price_usd} to regain lifetime-of-term access — your progress is saved.</>
          ) : (
            <>Welcome, {user?.name}. A one-time payment of ${overview.price_usd} gives you <strong>one year</strong> to complete all 90 chapters, every quiz, the final certification exam, and your certificate.</>
          )}
        </p>
        <button data-testid="dashboard-enroll-btn" onClick={enroll} disabled={enrolling}
          className="mt-8 px-8 py-4 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold hover:bg-[#223B31] transition-all disabled:opacity-60">
          {enrolling ? "Preparing checkout…" : expired ? `Re-enroll — $${overview.price_usd}` : `Enroll — $${overview.price_usd}`}
        </button>
        <div className="mt-10"><DisclaimerBanner /></div>
      </div>
    );
  }

  const completed = new Set(progress?.completed_chapters || []);
  const highestUnlocked = (() => {
    let n = 1;
    const total = overview.total_chapters;
    while (n <= total && completed.has(n)) n++;
    return user?.role === "admin" ? total : Math.min(n, total);
  })();

  const currentChapter = Math.min(highestUnlocked, overview.total_chapters);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-widest font-mono text-[#829B88]">Your Dashboard</span>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl text-[#1A2621]">Hello, {user?.name}</h1>
          {user?.role === "student" && user?.days_remaining != null && (
            <span data-testid="access-remaining" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#2C4A3E] bg-[#E8ECE8] px-3 py-1.5 rounded-full">
              <CalendarClock className="w-3.5 h-3.5" />
              {user.days_remaining} days left · access until {new Date(user.access_expires_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
            </span>
          )}
        </div>
        <button data-testid="continue-learning-btn" onClick={() => navigate(`/chapter/${currentChapter}`)}
          className="px-6 py-3 rounded-full bg-[#2C4A3E] text-[#FAF8F5] font-semibold hover:bg-[#223B31] transition-all flex items-center gap-2 self-start sm:self-auto">
          <PlayCircle className="w-5 h-5" /> {completed.size === 0 ? "Start Chapter 1" : `Continue · Chapter ${currentChapter}`}
        </button>
      </div>

      {/* Progress */}
      <div className="mt-8 bg-[#F3EFEA] border border-[#2C4A3E]/12 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="font-medium text-[#2C4A3E]">Overall progress</span>
          <span data-testid="progress-percent" className="font-mono text-sm text-[#2C4A3E]">{progress?.percent || 0}%</span>
        </div>
        <Progress value={progress?.percent || 0} className="h-3 bg-[#E8ECE8]" />
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat icon={BookOpen} label="Chapters done" value={`${progress?.completed_count || 0}/${overview.total_chapters}`} />
          <Stat icon={GraduationCap} label="Final exam" value={progress?.final_exam_passed ? "Passed" : progress?.final_exam_unlocked ? "Unlocked" : "Locked"} />
          <Stat icon={Award} label="Certificate" value={progress?.certificate_unlocked ? "Ready" : "Locked"} />
          <Stat icon={Leaf} label="Current" value={`Ch. ${currentChapter}`} />
        </div>
      </div>

      {/* Final exam + certificate */}
      <div className="mt-6 grid md:grid-cols-2 gap-5">
        <ActionCard
          testid="final-exam-card"
          icon={GraduationCap}
          title="Final Certification Exam"
          desc="35 questions spanning the whole course. Score 80% or more to certify."
          locked={!progress?.final_exam_unlocked}
          lockedText="Complete all 90 chapters to unlock"
          cta={progress?.final_exam_passed ? "Review / Retake" : "Take the exam"}
          onClick={() => navigate("/final-exam")}
          badge={progress?.final_exam_passed ? "Passed" : null}
        />
        <ActionCard
          testid="certificate-card"
          icon={Award}
          title="Your Certificate"
          desc="A personalized, print-worthy certificate of completion."
          locked={!progress?.certificate_unlocked}
          lockedText="Pass the final exam to unlock"
          cta="View certificate"
          onClick={() => navigate("/certificate")}
        />
      </div>

      {/* Chapters by module */}
      <h2 className="mt-12 mb-4 font-serif text-2xl text-[#1A2621]">Course chapters</h2>
      <div className="space-y-8">
        {overview.modules.map((m) => {
          const chs = overview.chapters.filter((c) => c.module_id === m.module_id);
          return (
            <div key={m.module_id}>
              <h3 className="font-serif text-lg text-[#2C4A3E] mb-3 flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-[#2C4A3E] text-[#FAF8F5] flex items-center justify-center font-mono text-xs">{m.module_id}</span>
                {m.title}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {chs.map((c) => {
                  const isDone = completed.has(c.chapter_number);
                  const isLocked = c.chapter_number > highestUnlocked;
                  return (
                    <button
                      key={c.chapter_number}
                      data-testid={`chapter-card-${c.chapter_number}`}
                      disabled={isLocked}
                      onClick={() => navigate(`/chapter/${c.chapter_number}`)}
                      className={`text-left p-4 rounded-xl border transition-all flex items-start gap-3 ${
                        isLocked
                          ? "bg-[#EBE5DC]/50 border-[#2C4A3E]/8 cursor-not-allowed opacity-70"
                          : "bg-[#F3EFEA] border-[#2C4A3E]/12 hover:-translate-y-0.5 hover:shadow-md"
                      }`}
                    >
                      <span className="mt-0.5 shrink-0">
                        {isDone ? <CheckCircle2 className="w-5 h-5 text-[#3D7A5A]" /> : isLocked ? <Lock className="w-5 h-5 text-[#829B88]" /> : <BookOpen className="w-5 h-5 text-[#2C4A3E]" />}
                      </span>
                      <span>
                        <span className="font-mono text-xs text-[#829B88]">Chapter {c.chapter_number}</span>
                        <span className="block text-sm font-medium text-[#1A2621]">{c.title}</span>
                        {isDone && <span className="text-xs text-[#3D7A5A]">Passed · {progress?.quiz_scores?.[c.chapter_number]}%</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-xl bg-[#E8ECE8] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#2C4A3E]" />
      </span>
      <div>
        <div className="text-xs text-[#5A6B61]">{label}</div>
        <div className="font-semibold text-[#1A2621]">{value}</div>
      </div>
    </div>
  );
}

function ActionCard({ testid, icon: Icon, title, desc, locked, lockedText, cta, onClick, badge }) {
  return (
    <div data-testid={testid} className={`rounded-2xl p-6 border ${locked ? "bg-[#EBE5DC]/40 border-[#2C4A3E]/10" : "bg-[#F3EFEA] border-[#2C4A3E]/12"}`}>
      <div className="flex items-center justify-between">
        <Icon className={`w-7 h-7 ${locked ? "text-[#829B88]" : "text-[#2C4A3E]"}`} />
        {badge && <span className="text-xs font-semibold text-[#3D7A5A] bg-[#E8ECE8] px-2.5 py-1 rounded-full">{badge}</span>}
        {locked && <Lock className="w-5 h-5 text-[#829B88]" />}
      </div>
      <h3 className="mt-4 font-serif text-xl text-[#1A2621]">{title}</h3>
      <p className="text-sm text-[#5A6B61] mt-1">{desc}</p>
      <button
        disabled={locked}
        onClick={onClick}
        data-testid={`${testid}-action`}
        className={`mt-4 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
          locked ? "bg-[#E8ECE8] text-[#829B88] cursor-not-allowed" : "bg-[#2C4A3E] text-[#FAF8F5] hover:bg-[#223B31]"
        }`}
      >
        {locked ? lockedText : cta}
      </button>
    </div>
  );
}
